import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, RefreshCw, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { api, TIPOS, ESTADOS_TTO, OPC, BONO_AVISO, TRIMESTRAL, today, fmtEur, fmtDate, avisoSiguiente } from "@/lib/api";
import { EstadoBadge } from "@/components/Badges";
import { useAuth, puedeEco } from "@/lib/auth";
import { Section, TextField, SelectField, DateField, DataList } from "@/components/Fields";
import { ItemsEditor } from "@/components/ItemsEditor";
import { PagosEditor } from "@/components/PagosEditor";
import { FacturaDialog } from "@/components/eco/FacturaDialog";
import { CitasAgendaTto } from "@/components/CitasAgendaTto";


const ControlSesiones = ({ c, mod, on5 }) => (
  <div data-testid="control-sesiones" className={`mb-4 max-w-2xl rounded-lg border px-4 py-3 text-sm flex flex-wrap items-center gap-x-6 gap-y-1 ${c.recuperar ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
    <span>{mod === "MENSUALIDAD (SEMANAL)" ? "Mensualidad: 4 sesiones al mes" : "Quincenal: 2 sesiones al mes"}</span>
    <span data-testid="ctrl-mes">Este mes: <b>{c.mes_hechas} de {c.mes_esperadas}</b></span>
    {c.previas > 0 && <span data-testid="ctrl-previas">Incluye {c.previas} hechas antes de la app</span>}
    {mod === "MENSUALIDAD (SEMANAL)" && <button type="button" onClick={on5} data-testid="ctrl-mes-5-btn" className="rounded border border-current px-2 py-0.5 text-xs">{c.mes_esperadas === 5 ? "Volver a 4 este mes" : "Este mes son 5 sesiones"}</button>}
    <span data-testid="ctrl-total">Desde {fmtDate(c.inicio)}: {c.hechas} hechas de {c.esperadas} que tocaban</span>
    <span data-testid="ctrl-recuperar" className="font-semibold">{c.recuperar ? `${c.recuperar} por recuperar` : "Al día"}</span>
  </div>
);

const NoEsBono = ({ onConvertir }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col justify-end">
      {!open ? <button type="button" onClick={() => setOpen(true)} data-testid="tf-convertir-btn" className="h-9 rounded-md border border-rose-300 px-3 text-xs text-rose-700 hover:bg-rose-50">No es bono: cambiar tipo</button>
        : <div className="flex flex-wrap gap-1" data-testid="tf-convertir-opciones">
          {OPC.modalidad_ses.map((m) => <button key={m} type="button" onClick={() => { setOpen(false); onConvertir(m); }} data-testid={`tf-convertir-${m.split(" ")[0].toLowerCase()}`} className="rounded-md bg-rose-700 px-2 py-1 text-[11px] text-white">{m}</button>)}
          <button type="button" onClick={() => setOpen(false)} className="text-[11px] underline text-slate-500">Cancelar</button>
        </div>}
    </div>
  );
};

const Config = ({ f, set, setModalidad, isAdmin, onConvertir }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Estado</span>
      <select data-testid="tf-estado" className="field-select" value={f.estado} onChange={(e) => set("estado")(e.target.value)}>
        {ESTADOS_TTO.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
      </select>
    </label>
    <DateField label="Fecha inicio" value={f.fecha_inicio} onChange={set("fecha_inicio")} testId="tf-fecha-inicio" />
    {isAdmin && <TextField label={f.tipo === "sesiones" ? (f.modalidad === "MENSUALIDAD (SEMANAL)" ? "Cuota mensual €" : "Precio por sesión €") : "Precio total €"} type="number" value={f.precio} onChange={set("precio")} testId="tf-precio" />}
    {f.tipo === "sesiones" && <SelectField label="Tipo de sesión" value={f.modalidad} onChange={set("modalidad")} options={OPC.modalidad_ses} testId="tf-modalidad-ses" />}
    {f.tipo === "sesiones" && f.modalidad === "MENSUALIDAD (SEMANAL)" && <TextField label="Día de la sesión semanal" value={f.hueco} onChange={set("hueco")} testId="tf-hueco-ses" placeholder="Martes 17:00" />}
    {f.tipo === "sesiones" && f.modalidad !== "SESIÓN SUELTA" && <TextField label="Sesiones hechas antes de la app" type="number" value={f.sesiones_previas ?? 0} onChange={set("sesiones_previas")} testId="tf-previas-ses" />}
    <TextField label="Forma de pago" value={f.forma_pago} onChange={set("forma_pago")} testId="tf-forma-pago" />
    {f.tipo === "bono" && (
      <>
        <SelectField label="Modalidad" value={f.modalidad} onChange={setModalidad} options={OPC.modalidad} testId="tf-modalidad" />
        <NoEsBono onConvertir={onConvertir} />
        <TextField label={f.modalidad === "BONO TRATAMIENTO COMPLETO" ? "Sesiones vendidas" : "Sesiones presupuestadas"} type="number" value={f.sesiones_presupuestadas} onChange={set("sesiones_presupuestadas")} testId="tf-presupuestadas" />
        <TextField label="Sesiones previas (Excel)" type="number" value={f.sesiones_previas} onChange={set("sesiones_previas")} testId="tf-previas" />
        <TextField label="Bonos anteriores (antes de la app)" type="number" value={f.bonos_previos ?? 0} onChange={set("bonos_previos")} testId="tf-bonos-previos" />
        <TextField label="Hueco (día/hora)" value={f.hueco} onChange={set("hueco")} testId="tf-hueco" placeholder="Lunes 16:30" />
      </>
    )}
    {f.tipo === "bono" && f.modalidad === TRIMESTRAL && (
      <>
        <DateField label="Programa casa desde" value={f.plataforma_inicio} onChange={set("plataforma_inicio")} testId="tf-plataforma-inicio" />
        <DateField label="Programa casa hasta" value={f.plataforma_fin} onChange={set("plataforma_fin")} testId="tf-plataforma-fin" />
      </>
    )}
    {f.tipo === "syntonic" && (
      <div className="block" data-testid="tf-aparato">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Aparato</span>
        <div className="h-9 flex items-center text-sm">
          {f.device_id ? <b className="text-amber-700">{f.aparato} (prestado)</b> : f.aparato_devuelto ? <span className="text-slate-500">{f.aparato} · devuelto</span> : <span className="text-slate-400">Sin aparato · en espera</span>}
          <Link to="/tratamientos/syntonic" className="ml-2 text-xs text-[#1E3A8A] underline">Gestionar</Link>
        </div>
      </div>
    )}
  </div>
);

export default function TratamientoDetalle() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [t, setT] = useState(null);
  const [f, setF] = useState(null);
  const isAdmin = puedeEco(useAuth().user);
  const [facturar, setFacturar] = useState(false);
  const load = useCallback(() => {
    api.get(`/treatments/${id}`).then((r) => { setT(r.data); setF(r.data); });
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  if (!f) return <div className="text-slate-400 text-sm">Cargando…</div>;

  const T = TIPOS[f.tipo];
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setModalidad = (v) => setF((p) => {
    if (v !== TRIMESTRAL) return { ...p, modalidad: v, ...(v === "BONO 14 SESIONES" && Number(p.sesiones_presupuestadas) === 6 ? { sesiones_presupuestadas: 14 } : {}) };
    const ini = p.plataforma_inicio || p.fecha_inicio || today();
    const fin = new Date(ini);
    fin.setMonth(fin.getMonth() + 3);
    return { ...p, modalidad: v, sesiones_presupuestadas: 6, plataforma_inicio: ini, plataforma_fin: p.plataforma_fin || fin.toISOString().slice(0, 10) };
  });
  const save = async () => {
    const { data } = await api.put(`/treatments/${id}`, f);
    setT({ ...data, patient: t.patient });
    setF({ ...data, patient: t.patient });
    toast.success("Tratamiento guardado");
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar este tratamiento?")) return;
    await api.delete(`/treatments/${id}`);
    toast.success("Tratamiento eliminado");
    nav(`/tratamientos/${f.tipo}`);
  };
  const finalizar = async () => {
    const msg = f.device_id ? `¿Finalizar ${T.single}? El aparato ${f.aparato} se marcará como devuelto y quedará libre.` : `¿Marcar ${T.single} como finalizado?`;
    if (!window.confirm(msg)) return;
    await api.put(`/treatments/${id}`, f);
    const { data } = await api.post(`/treatments/${id}/finalizar`);
    toast.success(data.aparato_devuelto ? `Tratamiento finalizado · ${data.aparato_devuelto} libre` : "Tratamiento finalizado");
    avisoSiguiente(data, toast, nav);
    load();
  };
  const renovar = async () => {
    const que = f.tipo === "bono" ? "el bono" : f.modalidad;
    if (!window.confirm(`¿Renovar ${que}? El actual se cierra como finalizado y se crea uno nuevo desde hoy con la misma modalidad, precio y hueco.`)) return;
    await api.put(`/treatments/${id}`, f);
    const { data } = await api.post(`/treatments/${id}/renovar`);
    toast.success(f.tipo === "bono" ? "Bono renovado" : "Renovado");
    nav(`/tratamiento/${data.id}`);
  };
  const toggle5 = async () => {
    const mes = new Date().toISOString().slice(0, 7);
    const m5 = f.meses_5 || [];
    await api.put(`/treatments/${id}`, { ...f, meses_5: m5.includes(mes) ? m5.filter((x) => x !== mes) : [...m5, mes] });
    toast.success(m5.includes(mes) ? "Este mes vuelve a 4 sesiones" : "Este mes cuenta 5 sesiones");
    load();
  };
  const convertir = async (modalidad) => {
    if (!window.confirm(`¿Pasar a ${modalidad}? Saldrá de Bonos y aparecerá en Sesiones sin bono (se conservan sesiones, fechas y pagos).`)) return;
    await api.put(`/treatments/${id}`, f);
    await api.post("/treatments/convertir", { ids: [id], modalidad });
    toast.success(`Cambiado a ${modalidad}: ahora está en Sesiones sin bono`, { duration: 6000 });
    load();
  };
  const activo = ["pendiente", "en_proceso"].includes(t.estado);
  const hechas = f.citas.filter((c) => c.realizada).length + (f.tipo === "bono" || t.control ? Number(f.sesiones_previas || 0) : 0);
  const total = f.tipo === "bono" ? Number(f.sesiones_presupuestadas || 0) : t.control ? Math.max(t.control.esperadas, hechas) : f.citas.length;
  const pagado = (f.pagos || []).reduce((s, p) => s + Number(p.importe || 0), 0);

  return (
    <div data-testid="treatment-detail-page">
      {loc.state?.desdePaciente && t.patient
        ? <Link to={`/pacientes/${t.patient.id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-patient"><ArrowLeft className="h-4 w-4" />Volver a {t.patient.nombre}</Link>
        : <Link to={`/tratamientos/${f.tipo}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-treatments"><ArrowLeft className="h-4 w-4" />{T.label}</Link>}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 fade-up">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md grid place-items-center" style={{ background: `${T.dot}14`, color: T.dot }}><T.icon className="h-5 w-5" /></div>
          <div>
            <h1 className="font-[Outfit] text-3xl font-bold tracking-tight text-slate-900">{T.single}{t.bono_num ? ` nº ${t.bono_num}` : ""}</h1>
            {t.bono_num && <span data-testid="bono-num" className="inline-block mr-2 rounded bg-indigo-50 text-indigo-800 text-xs font-semibold px-2 py-0.5">Lleva {t.bonos_total} {t.bonos_total === 1 ? "bono" : "bonos"}</span>}
            <Link to={`/pacientes/${t.patient.id}`} data-testid="treatment-patient-link" className="text-sm text-[#1E3A8A] hover:underline">{t.patient.nombre}</Link>
            <span className="text-sm text-slate-400"> · {t.patient.centro}</span> <EstadoBadge estado={t.estado} testId="treatment-estado" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={remove} data-testid="delete-treatment-btn" className="text-rose-700"><Trash2 className="h-4 w-4" /></Button>
          {activo && f.tipo === "bono" && <Button onClick={renovar} data-testid="renovar-bono-btn" className="bg-[#0D9488] hover:bg-teal-700"><RefreshCw className="h-4 w-4 mr-1" />Renovar bono</Button>}
          {activo && f.tipo === "sesiones" && f.modalidad !== "SESIÓN SUELTA" && <Button onClick={renovar} data-testid="renovar-sesiones-btn" className="bg-[#0D9488] hover:bg-teal-700"><RefreshCw className="h-4 w-4 mr-1" />Renovar</Button>}
          {activo && <Button variant="outline" onClick={finalizar} data-testid="finalizar-treatment-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4 mr-1" />{f.tipo === "bono" ? "Finalizar bono" : f.tipo === "syntonic" ? "Syntonic finalizado" : `Terminar ${T.single}`}</Button>}
          <Button onClick={save} data-testid="save-treatment-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Save className="h-4 w-4 mr-1" />Guardar</Button>
        </div>
      </div>

      {f.tipo === "bono" && f.estado === "en_proceso" && total > 0 && total - hechas <= BONO_AVISO && (
        <div data-testid="bono-aviso-banner" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 font-medium flex flex-wrap items-center gap-3">
          <span className="flex-1">Atención: {total - hechas <= 0 ? "el bono está agotado" : `quedan ${total - hechas} sesiones del bono`}. Habla con la familia para renovarlo.</span>
          <Button size="sm" onClick={renovar} data-testid="banner-renovar-btn" className="bg-[#0D9488] hover:bg-teal-700">Renovar</Button>
          <Button size="sm" variant="outline" onClick={finalizar} data-testid="banner-finalizar-btn">Finalizar</Button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3 mb-4 max-w-2xl">
        <div className="bg-white border rounded-lg px-4 py-3" data-testid="stat-progreso">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{f.tipo === "bono" ? "Sesiones" : "Citas"}</div>
          <div className="font-[JetBrains_Mono] text-xl font-semibold">{hechas}/{total}</div>
          <Progress value={total ? (hechas / total) * 100 : 0} className="h-1.5 mt-2" />
        </div>
        {isAdmin && <div className="bg-white border rounded-lg px-4 py-3" data-testid="stat-pagado">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Pagado</div>
          <div className="font-[JetBrains_Mono] text-xl font-semibold text-emerald-700">{fmtEur(pagado)}</div>
        </div>}
        {isAdmin && <div className="bg-white border rounded-lg px-4 py-3" data-testid="stat-pendiente">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Pendiente</div>
          <div className="font-[JetBrains_Mono] text-xl font-semibold text-amber-700">{fmtEur(t.pendiente_pago)}</div>
        </div>}
      </div>
      {t.control && <ControlSesiones c={t.control} mod={t.modalidad} on5={toggle5} />}

      <div className="space-y-4">
        <Section title="Configuración" testId="sec-config"><Config f={f} set={set} setModalidad={setModalidad} isAdmin={isAdmin} onConvertir={convertir} /></Section>
        <Section title={f.tipo === "bono" ? "Sesiones" : "Citas y fechas"} testId="sec-citas">
          <ItemsEditor items={f.citas} onChange={set("citas")} prefix="cita"
            addLabel={f.tipo === "bono" || f.tipo === "sesiones" ? "Programar sesión" : f.tipo === "syntonic" ? "Añadir revisión" : "Añadir cita"}
            nextLabel={f.tipo === "bono" ? (items) => `Sesión ${items.length + Number(f.sesiones_previas || 0) + 1}`
              : f.tipo === "sesiones" ? (items) => `Sesión ${items.length + 1}`
              : f.tipo === "syntonic" ? (items) => `${items.filter((c) => /revisi/i.test(c.etiqueta)).length + 1}ª Revisión` : undefined}
            showToday={f.tipo !== "syntonic"}
            extra={f.tipo === "syntonic" && activo ? <Button type="button" size="sm" onClick={finalizar} data-testid="syntonic-finalizado-btn" className="bg-[#0D9488] hover:bg-teal-700"><CheckCircle2 className="h-4 w-4 mr-1" />Syntonic finalizado</Button> : null} />
        </Section>
        <Section title="Citas de la agenda" testId="sec-citas-agenda"><CitasAgendaTto tid={t.id} /></Section>
        {isAdmin && <Section title="Pagos" testId="sec-pagos">
          <PagosEditor pagos={f.pagos} onChange={set("pagos")} />
          <div className="mt-3 flex items-center gap-3">
            <Button type="button" size="sm" variant="outline" onClick={() => setFacturar(true)} disabled={JSON.stringify(f.pagos) !== JSON.stringify(t.pagos)} data-testid="tto-facturar">Facturar</Button>
            <span className="text-[11px] text-slate-500">{JSON.stringify(f.pagos) !== JSON.stringify(t.pagos) ? "Guarda los pagos antes de facturar." : `${(t.pagos || []).filter((p) => !p.factura).length} pagos sin facturar`}</span>
          </div>
          <FacturaDialog open={facturar} onClose={() => setFacturar(false)} preset={{ patient: t.patient, treatmentId: t.id }} />
        </Section>}
        <Section title="Notas de seguimiento" testId="sec-notas">
          <Textarea data-testid="tf-notas" rows={4} value={f.notas} onChange={(e) => set("notas")(e.target.value)} className="text-sm" />
        </Section>
        <div className="flex justify-end"><Button onClick={save} data-testid="save-treatment-btn-bottom" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Save className="h-4 w-4 mr-1" />Guardar cambios</Button></div>
      </div>
    </div>
  );
}
